// ----------------------------------------------------------------------------
// Snippet: Country Switcher
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------
import './_country-switcher.scss';
var WEGLOT_READY_INTERVAL_MS = 50;
var WEGLOT_READY_TIMEOUT_MS = 30000;

/**
 * Weglot exposes switchTo before options.languages is populated (async project fetch).
 * Wait for switchTo + 2+ languages, via initialized event and polling.
 */
function whenWeglotLanguagesReady(callback) {
  var _window$Weglot;
  var done = false;
  var pollId = null;
  var tryFinish = () => {
    if (done) return true;
    var _window = window,
      Weglot = _window.Weglot;
    if (!(Weglot !== null && Weglot !== void 0 && Weglot.switchTo)) return false;
    var languages = getWeglotLanguages();
    if (languages.length < 1) return false;
    done = true;
    if (pollId) clearInterval(pollId);
    callback();
    return true;
  };
  if (tryFinish()) return;
  if ((_window$Weglot = window.Weglot) !== null && _window$Weglot !== void 0 && _window$Weglot.on) {
    window.Weglot.on('initialized', tryFinish);
  }
  var started = Date.now();
  pollId = setInterval(() => {
    if (tryFinish()) return;
    if (Date.now() - started >= WEGLOT_READY_TIMEOUT_MS) {
      clearInterval(pollId);
    }
  }, WEGLOT_READY_INTERVAL_MS);
}
function getWeglotLanguages() {
  var _window2 = window,
    Weglot = _window2.Weglot;
  if (!(Weglot !== null && Weglot !== void 0 && Weglot.options)) return [];
  var _Weglot$options = Weglot.options,
    languages = _Weglot$options.languages,
    languageFrom = _Weglot$options.language_from;
  var translated = (languages || []).map(language => language.language_to);
  var all = [...translated, languageFrom].filter(Boolean);
  return [...new Set(all)];
}

// -------------------------------------
// DisclosureDropdown
// Handles the expansion store country list (plain links, no form submit).
// -------------------------------------
class DisclosureDropdown extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('[aria-controls]');
    this.panelId = this.button ? this.button.getAttribute('aria-controls') : null;
    this.panel = this.panelId ? document.getElementById(this.panelId) : null;
    if (!this.button || !this.panel) return;
    this._onToggleBound = this.toggle.bind(this);
    this._onKeyUpBound = this.onKeyUp.bind(this);
    this._onOutsideClickBound = this.onOutsideClick.bind(this);
    this.button.addEventListener('click', this._onToggleBound);
    this.addEventListener('keyup', this._onKeyUpBound);
    document.addEventListener('click', this._onOutsideClickBound);
  }
  get isOpen() {
    return this.button.getAttribute('aria-expanded') === 'true';
  }
  open() {
    this.button.setAttribute('aria-expanded', 'true');
    this.panel.removeAttribute('hidden');
  }
  close() {
    this.button.setAttribute('aria-expanded', 'false');
    this.panel.setAttribute('hidden', '');
  }
  toggle() {
    this.isOpen ? this.close() : this.open();
  }
  onKeyUp(event) {
    if (event.code.toUpperCase() === 'ESCAPE' && this.isOpen) {
      this.close();
      this.button.focus();
    }
  }
  onOutsideClick(event) {
    if (!this.contains(event.target) && this.isOpen) {
      this.close();
    }
  }
  disconnectedCallback() {
    if (this._onOutsideClickBound) {
      document.removeEventListener('click', this._onOutsideClickBound);
    }
    if (this.button && this._onToggleBound) {
      this.button.removeEventListener('click', this._onToggleBound);
    }
    if (this._onKeyUpBound) {
      this.removeEventListener('keyup', this._onKeyUpBound);
    }
  }
}
customElements.define('disclosure-dropdown', DisclosureDropdown);

// -------------------------------------
// WeglotLanguageDisclosure
// Language switcher driven by Weglot
// -------------------------------------
class WeglotLanguageDisclosure extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button[aria-controls]');
    this.currentLangLabel = this.querySelector('[data-weglot-current-lang]');
    this.panelId = this.button ? this.button.getAttribute('aria-controls') : null;
    this.panel = this.panelId ? document.getElementById(this.panelId) : null;
    if (!this.button || !this.panel || !this.currentLangLabel) return;
    this._onToggleBound = this.toggle.bind(this);
    this._onKeyUpBound = this.onKeyUp.bind(this);
    this._onOutsideClickBound = this.onOutsideClick.bind(this);
    this._onLanguageClickBound = this.onLanguageClick.bind(this);
    this._onLanguageChangedBound = this.onLanguageChanged.bind(this);
    this.button.addEventListener('click', this._onToggleBound);
    this.addEventListener('keyup', this._onKeyUpBound);
    document.addEventListener('click', this._onOutsideClickBound);
    this._weglotInitialized = false;
    whenWeglotLanguagesReady(() => this.initWeglot());
  }
  get isOpen() {
    return this.button.getAttribute('aria-expanded') === 'true';
  }
  initWeglot() {
    if (this._weglotInitialized) return;
    this._weglotInitialized = true;
    var languages = getWeglotLanguages();
    var currentLang = window.Weglot.getCurrentLang();
    if (languages.length >= 2) {
      this.buildLanguageList(languages);
    } else {
      this.panel.setAttribute('hidden', '');
      this.button.setAttribute('hidden', '');
    }
    this.updateCurrentLanguage(currentLang);
    this.removeAttribute('hidden');
    window.Weglot.on('languageChanged', this._onLanguageChangedBound);
  }
  buildLanguageList(languages) {
    var fragment = document.createDocumentFragment();
    languages.forEach(code => {
      var name = window.Weglot.getLanguageName(code);
      var item = document.createElement('li');
      item.className = 'disclosure__item';
      item.setAttribute('tabindex', '-1');
      var link = document.createElement('a');
      link.href = '#';
      link.setAttribute('data-value', code);
      link.setAttribute('hreflang', code);
      link.setAttribute('lang', code);
      link.setAttribute('aria-label', name);
      link.textContent = name;
      link.addEventListener('click', this._onLanguageClickBound);
      item.appendChild(link);
      fragment.appendChild(item);
    });
    this.panel.replaceChildren(fragment);
  }
  updateCurrentLanguage(lang) {
    var _window3 = window,
      Weglot = _window3.Weglot;
    var name = Weglot.getLanguageName(lang);
    this.currentLangLabel.textContent = name;
    var ariaPrefix = this.button.dataset.currentLanguageAriaLabel;
    this.button.setAttribute('aria-label', ariaPrefix ? "".concat(ariaPrefix, ": ").concat(name) : name);
    this.panel.querySelectorAll('a[data-value]').forEach(link => {
      if (link.dataset.value === lang) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }
  onLanguageClick(event) {
    event.preventDefault();
    var code = event.currentTarget.dataset.value;
    if (!code) return;
    window.Weglot.switchTo(code);
    this.close();
  }
  onLanguageChanged(lang) {
    this.updateCurrentLanguage(lang);
  }
  open() {
    this.button.setAttribute('aria-expanded', 'true');
    this.panel.removeAttribute('hidden');
  }
  close() {
    this.button.setAttribute('aria-expanded', 'false');
    this.panel.setAttribute('hidden', '');
  }
  toggle() {
    this.isOpen ? this.close() : this.open();
  }
  onKeyUp(event) {
    if (event.code.toUpperCase() === 'ESCAPE' && this.isOpen) {
      this.close();
      this.button.focus();
    }
  }
  onOutsideClick(event) {
    if (!this.contains(event.target) && this.isOpen) {
      this.close();
    }
  }
  disconnectedCallback() {
    var _window$Weglot2;
    if (this._onOutsideClickBound) {
      document.removeEventListener('click', this._onOutsideClickBound);
    }
    if (this.button && this._onToggleBound) {
      this.button.removeEventListener('click', this._onToggleBound);
    }
    if (this._onKeyUpBound) {
      this.removeEventListener('keyup', this._onKeyUpBound);
    }
    if ((_window$Weglot2 = window.Weglot) !== null && _window$Weglot2 !== void 0 && _window$Weglot2.off && this._onLanguageChangedBound) {
      window.Weglot.off('languageChanged', this._onLanguageChangedBound);
    }
  }
}
customElements.define('weglot-language-disclosure', WeglotLanguageDisclosure);